import { groupBy } from "ramda";
import { type FC } from "react";

import { Card, CardRowSimple, type GridCard, HStack, Tag, TagIntent } from "widgets";

import { toTitleCase } from "../../../app-utils/string.js";
import { type Classification } from "../../../graphql.js";
import { GenericTile, NoteTile } from "../../core/tiles/index.js";

export interface IOntologyListCardProps extends GridCard {
  classifications: Classification[];
  title?: string;
}

const OntologyListCard: FC<IOntologyListCardProps> = (props) => {
  const count = (props.classifications || []).length;

  if (count === 0) {
    return null;
  }

  if (count === 0) {
    return (
      <Card
        title={props.title ? toTitleCase(props.title, ["GO", "NCBI"]) : "Classification Schemes"}
        tag={{ label: String(count) }}
      >
        No ontologies assigned.
      </Card>
    );
  }

  const groupedClassifications = groupBy((classification) => classification.schemeName, props.classifications ?? []);

  return (
    <Card
      title={props.title ? toTitleCase(props.title, ["GO", "NCBI"]) : "Classification Schemes"}
      tag={{ label: String(count) }}
      shrinkable={count > 7}
      icon={"share"}
    >
      {Object.keys(groupedClassifications).map((classificationGroupName) => {
        const classifications = groupedClassifications[classificationGroupName];
        return (
          <>
            <CardRowSimple
              label={
                <HStack spacing={10}>
                  {classificationGroupName}
                  <Tag label={String(classifications.length)} intentColor={TagIntent.Outlined} />
                </HStack>
              }
            >
              {classifications.map((classification: Classification) => {
                // TODO: Check what to do here: Link (to which page, external tab?), popover, just text?
                // TODO: If linked: Use URL from system params?
                const url = classification.name?.startsWith("GO")
                  ? `http://amigo.geneontology.org/amigo/term/${classification.name}`
                  : undefined;

                const description = classification.description ?? (url ? new URL(url).host : null);

                return url ? (
                  <GenericTile
                    title={classification.name}
                    to={url}
                    key={`ontology-card-div-${classification.id}`}
                    footNote={description}
                  />
                ) : (
                  <NoteTile title={classification.name} key={`ontology-card-div-${classification.id}`}>
                    {description}
                  </NoteTile>
                );
              })}
            </CardRowSimple>
          </>
        );
      })}
    </Card>
  );
};

export { OntologyListCard };
